import { useEffect, useMemo, useState } from 'react';
import * as RP from '@radix-ui/react-popover';
import { Check, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/cn';
import { Input } from './Field';

/**
 * Multi-select filter control.
 *
 * The catalog filters (department, brand, vendor) are CSV multi-selects on the
 * API, and a native `<select multiple>` is unusable at 400 departments. This is
 * a popover with a type-to-narrow list: closed it reads as one chip stating the
 * applied filter, open it is a dense checklist. Value lives in the URL as CSV,
 * so the component speaks arrays and the screen joins them.
 */

export interface MultiSelectOption {
  value: number | string;
  label: string;
  /** Quiet trailing text — a count, a code. */
  hint?: string;
}

export function MultiSelect({
  label,
  options,
  selected,
  onChange,
  loading = false,
  className,
  width = 260,
}: {
  label: string;
  options: MultiSelectOption[];
  selected: Array<number | string>;
  onChange: (next: Array<number | string>) => void;
  loading?: boolean;
  className?: string;
  width?: number;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, query]);

  const selectedSet = useMemo(() => new Set(selected.map((v) => String(v))), [selected]);

  const summary =
    selected.length === 0
      ? 'Any'
      : selected.length === 1
        ? (options.find((o) => String(o.value) === String(selected[0]))?.label ??
          '1 selected')
        : `${selected.length} selected`;

  function toggle(value: number | string) {
    const key = String(value);
    const next = selectedSet.has(key)
      ? selected.filter((v) => String(v) !== key)
      : [...selected, value];
    onChange(next);
  }

  return (
    <RP.Root
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setQuery('');
      }}
    >
      <RP.Trigger asChild>
        <button
          type="button"
          className={cn(
            'flex h-7 max-w-56 items-center gap-1.5 rounded-sm border px-2 text-xs transition-colors duration-75',
            selected.length > 0
              ? 'border-signal-line bg-signal-muted text-ink'
              : 'border-line bg-surface text-ink-muted hover:border-line-strong hover:text-ink',
            className
          )}
        >
          <span className="text-ink-faint shrink-0">{label}</span>
          <span className="truncate font-medium">{summary}</span>
          <ChevronDown className="size-3 shrink-0 opacity-60" />
        </button>
      </RP.Trigger>
      <RP.Portal>
        <RP.Content
          align="start"
          sideOffset={4}
          className="border-line-strong bg-surface-raised z-50 flex max-h-80 flex-col overflow-hidden rounded border shadow-lg shadow-black/30 data-[state=open]:animate-[pop-in_120ms_var(--ease-out-quint)]"
          style={{ width }}
        >
          <div className="border-line shrink-0 border-b p-1.5">
            <Input
              autoFocus
              size="sm"
              value={query}
              placeholder={`Filter ${label.toLowerCase()}…`}
              onChange={(e) => setQuery(e.currentTarget.value)}
            />
          </div>
          <div className="scrollpane min-h-0 flex-1 py-1">
            {loading ? (
              <div className="text-2xs text-ink-faint px-2 py-3">Loading…</div>
            ) : filtered.length === 0 ? (
              <div className="text-2xs text-ink-faint px-2 py-3">No matches.</div>
            ) : (
              filtered.map((opt) => {
                const on = selectedSet.has(String(opt.value));
                return (
                  <button
                    key={String(opt.value)}
                    type="button"
                    onClick={() => toggle(opt.value)}
                    className={cn(
                      'hover:bg-surface-hover flex w-full items-center gap-2 px-2 py-1 text-left text-xs',
                      on && 'text-ink'
                    )}
                  >
                    <span
                      className={cn(
                        'flex size-3.5 shrink-0 items-center justify-center rounded-xs border',
                        on
                          ? 'border-signal bg-signal text-ink-inverse'
                          : 'border-line-strong bg-surface-sunken'
                      )}
                    >
                      {on ? <Check className="size-2.5 stroke-[3]" /> : null}
                    </span>
                    <span className="min-w-0 flex-1 truncate">{opt.label}</span>
                    {opt.hint ? (
                      <span className="text-2xs text-ink-faint shrink-0 font-mono tabular-nums">
                        {opt.hint}
                      </span>
                    ) : null}
                  </button>
                );
              })
            )}
          </div>
          {selected.length > 0 ? (
            <div className="border-line shrink-0 border-t px-2 py-1.5">
              <button
                type="button"
                onClick={() => onChange([])}
                className="text-2xs text-ink-faint hover:text-ink"
              >
                Clear {label.toLowerCase()} filter
              </button>
            </div>
          ) : null}
        </RP.Content>
      </RP.Portal>
    </RP.Root>
  );
}

/**
 * Debounce a fast-changing value (a search box) before it becomes a query key
 * or a URL write. 250ms is the point where typing stays fluid but a five-letter
 * word still only costs one request.
 */
export function useDebouncedValue<T>(value: T, delay = 250): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

/** CSV ↔ number[] helpers for filter values that live in the URL. */
export function parseIdCsv(csv: string): number[] {
  return csv
    .split(',')
    .map((s) => Number(s.trim()))
    .filter((n) => Number.isFinite(n) && n > 0);
}

export function toIdCsv(values: Array<number | string>): string {
  return values.map((v) => String(v)).join(',');
}
